/**
 * Remote player ship rendered from predicted physics state.
 * Uses Rapier body positions (updated at 60Hz physics rate) with
 * velocity-based sub-tick extrapolation for smooth display-rate rendering.
 * Position: visualPos = physicsPos + velocity * alpha * TICK_DELTA
 * Rotation: exponential lerp (same as PlayerShip — no angular velocity data).
 */

import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Suspense } from 'react';
import type { ShipState } from '@warmancer/shared';
import { TICK_DELTA } from '@warmancer/shared';
import { Ship, PlaceholderShip } from './models/Ship';
import { ThrusterExhaust } from './effects/ThrusterExhaust';
import { MuzzleFlash } from './effects/MuzzleFlash';
import { useGameStore } from '../stores/gameStore';

interface RemoteShipProps {
  readonly state: ShipState;
  readonly stateTime: number;
  readonly username: string;
}

const MODEL_PATH = '/assets/models/ships/Fighter1.glb';
const SHIP_SIZE = 2;

// Rotation lerp speed: exponential lerp for rotation only (no angular velocity data).
// Matches PlayerShip ROTATION_LERP_SPEED for consistent behavior.
const ROTATION_LERP_SPEED = 50;

/**
 * Shortest-path angle interpolation handling the -PI/+PI wrap.
 */
function lerpAngle(from: number, to: number, t: number): number {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}

export function RemoteShip({ state }: RemoteShipProps) {
  const groupRef = useRef<THREE.Group>(null);
  const renderRotation = useRef({ value: 0, initialized: false });

  useFrame((_, delta) => {
    if (!groupRef.current) return;

    // Read predicted position from Rapier physics world (updated at 60Hz).
    // Falls back to snapshot state if predicted position is not yet available.
    const predicted = useGameStore.getState().remotePredictedPositions.get(state.entityId);
    const baseX = predicted ? predicted.x : state.x;
    const baseY = predicted ? predicted.y : state.y;
    const baseVx = predicted ? predicted.vx : state.vx;
    const baseVy = predicted ? predicted.vy : state.vy;
    const targetRotation = predicted ? predicted.rotation : state.rotation;
    const alpha = useGameStore.getState().renderAlpha;

    // Velocity-based sub-tick extrapolation for position
    const targetX = baseX + baseVx * alpha * TICK_DELTA;
    const targetZ = baseY + baseVy * alpha * TICK_DELTA;

    if (!renderRotation.current.initialized) {
      renderRotation.current.value = targetRotation;
      renderRotation.current.initialized = true;
    } else {
      // Exponential lerp for rotation only (no angular velocity from Rapier)
      const t = Math.min(1, ROTATION_LERP_SPEED * delta);
      renderRotation.current.value = lerpAngle(renderRotation.current.value, targetRotation, t);
    }

    groupRef.current.position.x = targetX;
    groupRef.current.position.z = targetZ;
    groupRef.current.rotation.y = -renderRotation.current.value - Math.PI / 2;
  });

  if (!state.alive) return null;

  return (
    <group ref={groupRef}>
      <Suspense fallback={<PlaceholderShip targetSize={SHIP_SIZE} />}>
        <Ship modelPath={MODEL_PATH} targetSize={SHIP_SIZE} />
      </Suspense>
      {/* Main engines (rear) */}
      <group position={[0, 0, 0.8]}>
        <ThrusterExhaust active={state.thrusting && !state.braking} afterburner={state.afterburner} />
      </group>
      {/* Brake thruster (center, beneath hull, reverse) */}
      <group position={[0, -0.15, 0]}>
        <ThrusterExhaust active={state.braking} afterburner={state.afterburner} reverse />
      </group>
      {/* Right-side thruster (fires when strafing LEFT — exhaust goes right, pushes ship left) */}
      <group position={[0, -0.15, 0]} rotation={[0, Math.PI / 2, 0]}>
        <ThrusterExhaust active={state.strafingLeft} afterburner={state.afterburner} />
      </group>
      {/* Left-side thruster (fires when strafing RIGHT — exhaust goes left, pushes ship right) */}
      <group position={[0, -0.15, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <ThrusterExhaust active={state.strafingRight} afterburner={state.afterburner} />
      </group>
      {/* Weapon muzzle flash at ship nose */}
      <group position={[0, 0.1, -1.0]}>
        <MuzzleFlash active={state.firing} />
      </group>
    </group>
  );
}
